import { ArrowIcon } from '@/atoms/Icons';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCalendarDate } from '@/utils/formatCalendarDate';
import cn from 'classnames';
import {
  forwardRef,
  MouseEvent,
  ReactElement,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styles from './Calendar.module.scss';

export enum CalendarType {
  Modal = 'modal',
  Block = 'block',
}

export type CalendarProps = {
  date: Date;
  onChange: (value: Date) => void;
  className?: string;
  type?: CalendarType;
  closeFn?: () => void;
  withYears?: boolean;
  title?: string;
  selectedDate?: Date;
  // При передаче availableDates, в календаре только они будут доступны
  availableDates?: { date: Date }[];
  // Не переключать дату при смене месяца
  noAutoChange?: boolean;
  // Доступными сделать только будущие даты
  onlyFuture?: boolean;
  // По умолчанию не будет выбрано даты, если параметр true
  noDefaultDate?: boolean;
  // Скрыть даты предыдущего и следующего месяца из календаря
  hideMonthDates?: boolean;
  // Ближайшая доступная дата через которую доступен выбор, при onlyFuture=true
  dateGap?: number;
  // Предотвращает визуальное выделение даты при клике (для календарей с подтверждением)
  preventVisualSelection?: boolean;
};

const generateYearsList = (endYear: number) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const years: number[] = [];
  for (let year = currentYear; year >= endYear; year--) {
    years.push(year);
  }
  return years;
};

export const Calendar = forwardRef(
  (
    {
      date,
      onChange,
      className,
      closeFn,
      type,
      withYears,
      title,
      selectedDate: externalSelectedDate,
      availableDates,
      noAutoChange,
      onlyFuture,
      noDefaultDate = false,
      hideMonthDates,
      dateGap = 4,
      preventVisualSelection = false,
    }: CalendarProps,
    ref,
  ) => {
    const wrapperClassName = cn(styles.wrapper, {
      [styles.modal]: type === CalendarType.Modal,
    });

    const monthClassName = cn(styles.month, {
      [styles.clickable]: withYears,
    });

    const calendarClassName = cn(
      styles.calendar,
      {
        [styles.calendarShadow]: type === CalendarType.Modal,
      },
      className,
    );
    const [currentDate, setCurrentDate] = useState(date);
    const [selectedDate, setSelectedDate] = useState<Date | null>(
      externalSelectedDate || (noDefaultDate ? null : date),
    );

    const reset = () => {
      setSelectedDate(null);
      setCurrentDate(new Date());
    };

    useImperativeHandle(ref, () => ({
      reset: reset,
    }));

    useEffect(() => {
      if (date) {
        setCurrentDate(date);
      }
    }, [date]);

    useEffect(() => {
      setSelectedDate(externalSelectedDate || null);
    }, [externalSelectedDate]);

    const [showYears, setShowYears] = useState(false);
    const YearsPopoverRef = useRef<HTMLDivElement>(null);
    const { locale } = useEnhancedRouter();
    const t = useTranslations();

    const daysOfWeek = [
      t.dayMon,
      t.dayTue,
      t.dayWed,
      t.dayThu,
      t.dayFri,
      t.daySat,
      t.daySun,
    ];

    const getDaysInMonth = (date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      return new Date(year, month + 1, 0).getDate();
    };

    const handleDateClick = (i: number) => {
      const value = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        i,
      );
      if (!preventVisualSelection) {
        setSelectedDate(value);
        setCurrentDate(value);
      }
      onChange(value);
    };

    const availableDatesSet = new Set(
      availableDates?.map((item) => {
        return new Date(item.date).toLocaleDateString('ru-RU');
      }),
    );

    useEffect(() => {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 4);

      if (onlyFuture && currentDate < futureDate) {
        setCurrentDate(futureDate);
      }
    }, []);

    const isDateAvailable = (date: Date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const futureDate = new Date(today);
      futureDate.setDate(futureDate.getDate() + dateGap);

      if (onlyFuture && date < futureDate) {
        return false;
      }

      if (availableDates === undefined) {
        if (date < new Date('31 Dec 1899 00:00:00 GMT')) return false;

        return true;
      }

      return availableDatesSet.has(date.toLocaleDateString('ru-RU'));
    };

    const renderDays = () => {
      const days: ReactElement[] = [];
      const daysInMonth = getDaysInMonth(currentDate);
      const firstDayOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1,
      ).getDay();

      const daysInPreviousMonth = getDaysInMonth(
        new Date(currentDate.getFullYear(), currentDate.getMonth() - 1),
      );

      const startingEmptyCells =
        firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

      for (
        let i = daysInPreviousMonth - startingEmptyCells + 1;
        i <= daysInPreviousMonth;
        i++
      ) {
        days.push(
          <div
            key={`prev-${i}`}
            className={cn(styles.dayCell, styles.disabled, {
              [styles.hidden]: hideMonthDates,
            })}
          >
            <div className={styles.dayCellCircle}>{i}</div>
          </div>,
        );
      }

      for (let i = 1; i <= daysInMonth; i++) {
        const isActive =
          selectedDate &&
          selectedDate.getDate() === i &&
          selectedDate.getMonth() === currentDate.getMonth() &&
          selectedDate.getFullYear() === currentDate.getFullYear();

        const isAvailable = isDateAvailable(
          new Date(currentDate.getFullYear(), currentDate.getMonth(), i),
        );

        days.push(
          <div
            key={i}
            className={cn(styles.dayCell, { [styles.disabled]: !isAvailable })}
            onClick={() => isAvailable && handleDateClick(i)}
          >
            <div
              className={cn(styles.dayCellCircle, {
                [styles.active]: isActive,
              })}
            >
              {i}
            </div>
          </div>,
        );
      }

      const lastDayOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0,
      ).getDay();

      const remainingEmptyCells = lastDayOfMonth === 0 ? 0 : 7 - lastDayOfMonth;

      for (let i = 1; i <= remainingEmptyCells; i++) {
        days.push(
          <div
            key={`next-${i}`}
            className={cn(styles.dayCell, styles.disabled, {
              [styles.hidden]: hideMonthDates,
            })}
          >
            <div className={styles.dayCellCircle}>{i}</div>
          </div>,
        );
      }

      return days;
    };

    const handlePrevMonth = () => {
      const newMonth = currentDate.getMonth() - 1;
      const newYear = currentDate.getFullYear() + (newMonth < 0 ? -1 : 0);
      const adjustedMonth = newMonth < 0 ? 11 : newMonth;

      const daysInNewMonth = new Date(newYear, adjustedMonth + 1, 0).getDate();
      const newDay = Math.min(currentDate.getDate(), daysInNewMonth);

      const newDate = new Date(newYear, adjustedMonth, newDay);
      setCurrentDate(newDate);

      if (!preventVisualSelection && !noAutoChange && selectedDate) {
        setSelectedDate(newDate);
        onChange(newDate);
      }
    };

    const handleNextMonth = () => {
      const newMonth = currentDate.getMonth() + 1;
      const newYear = currentDate.getFullYear() + (newMonth > 11 ? 1 : 0);
      const adjustedMonth = newMonth > 11 ? 0 : newMonth;

      const daysInNewMonth = new Date(newYear, adjustedMonth + 1, 0).getDate();
      const newDay = Math.min(currentDate.getDate(), daysInNewMonth);

      const newDate = new Date(newYear, adjustedMonth, newDay);
      setCurrentDate(newDate);

      if (!preventVisualSelection && !noAutoChange && selectedDate) {
        setSelectedDate(newDate);
        onChange(newDate);
      }
    };

    useEffect(() => {
      const body = document.querySelector('body');
      if (body && type === CalendarType.Modal) {
        body.classList.add('modal-opened');
      }

      return () => {
        if (body) {
          body.classList.remove('modal-opened');
        }
      };
    }, [type]);

    useEffect(() => {
      const handleClick = (event: globalThis.MouseEvent) => {
        const { target } = event;
        if (
          target instanceof Node &&
          !YearsPopoverRef.current?.contains(target)
        ) {
          setShowYears(false);
        }
      };

      window.addEventListener('click', handleClick);

      return () => {
        window.removeEventListener('click', handleClick);
      };
    }, [showYears]);

    useEffect(() => {
      if (availableDates && availableDates.length > 0) {
        const firstAvailableDate = new Date(availableDates[0].date);

        if (firstAvailableDate > currentDate)
          setCurrentDate(firstAvailableDate);
      }

      if (noAutoChange) {
        return;
      }

      if (availableDates && availableDates.length > 0) {
        const firstAvailableDate = new Date(availableDates[0].date);

        setCurrentDate(firstAvailableDate);

        onChange(firstAvailableDate);
      }
    }, [availableDates]);

    const handleShowYears = () => {
      if (!withYears) return;

      setShowYears((prev) => !prev);
    };

    const handleYearChange = (year: number) => {
      const newDate = new Date(currentDate);
      newDate.setFullYear(year);
      setCurrentDate(newDate);
      setShowYears(false);
    };

    return (
      <div className={wrapperClassName}>
        <div className={styles.blur} onClick={() => closeFn && closeFn()} />
        <div className={calendarClassName}>
          <h3 className={styles.title}>{title ?? t.selectDate}</h3>
          <div className={styles.header}>
            <ArrowIcon
              onClick={(event: MouseEvent) => {
                event.stopPropagation();
                handlePrevMonth();
              }}
              color="#18232E"
              className={styles.button}
            />
            <div className={styles.monthWrapper} ref={YearsPopoverRef}>
              <p
                className={monthClassName}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  handleShowYears();
                }}
              >
                {formatCalendarDate(currentDate, true, locale)}{' '}
                {withYears && (
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={showYears ? styles.reversed : ''}
                  >
                    <path
                      d="M4.5 6L8.5 10L12.5 6"
                      stroke="#18232E"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </p>
              {showYears && (
                <ul className={styles.yearsPopover}>
                  {generateYearsList(1900).map((year) => (
                    <li
                      key={year}
                      className={styles.yearsPopoverItem}
                      onClick={() => handleYearChange(year)}
                    >
                      {year}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <ArrowIcon
              onClick={(event: MouseEvent) => {
                event.stopPropagation();
                handleNextMonth();
              }}
              color="#18232E"
              className={styles.buttonNext}
            />
          </div>
          <div className={styles.daysOfWeekWrapper}>
            {daysOfWeek.map((day) => (
              <div key={day} className={styles.dayOfWeek}>
                {day}
              </div>
            ))}
          </div>
          <div className={styles.days}>{renderDays()}</div>
        </div>
      </div>
    );
  },
);

Calendar.displayName = 'Calendar';
